import { memo, useCallback, useState, type ComponentProps, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { useDraggable } from '@dnd-kit/core'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import type TripListView from './TripListView'
import TripListCheckboxButton from './TripListCheckboxButton'
import TripListMobileRowActionsMenu from './TripListMobileRowActionsMenu'
import TripListRowNote from './TripListRowNote'
import TripListRowPrice from './TripListRowPrice'
import TripListRowStatus from './TripListRowStatus'
import TripListTypeIcon from './TripListTypeIcon'
import { canMoveTripListRowToAnotherDay } from './tripListMoveDayOptions'
import { TRIP_LIST_MOBILE_INLINE_PADDING } from './tripListLayout'
import { getDragOverlayDraggableId } from '../tripDragReorder'
import type { TripListRow, TripListStatusValue } from './tripListTypes'

type TripListViewProps = ComponentProps<typeof TripListView>

interface Props {
  row: TripListRow
  tripStops: TripListViewProps['tripStops']
  selected: boolean
  selectionMode: boolean
  luxPlusEnabled: boolean
  isBookingPending: boolean
  isNoteUpdatePending: boolean
  isStatusUpdatePending: boolean
  notesDisabled: boolean
  statusDisabled: boolean
  actionsDisabled: boolean
  dragDisabled?: boolean
  isDragOverlay?: boolean
  dropTargetPlacement?: 'before' | 'after'
  onSelectRow: TripListViewProps['onSelectRow']
  onRowClick: TripListViewProps['onRowClick']
  onBookRow: TripListViewProps['onBookRow']
  onViewBooking: TripListViewProps['onViewBooking']
  onAskAi: TripListViewProps['onAskAi']
  onAskAiMove: TripListViewProps['onAskAiMove']
  onDelete: TripListViewProps['onDelete']
  onMoveToDay: TripListViewProps['onMoveToDay']
  onUpdateNote: TripListViewProps['onUpdateNote']
  onUpdateStatus: TripListViewProps['onUpdateStatus']
}

const Card = styled.article`
  position: relative;
  display: grid;
  grid-template-columns: ${rem(24)} ${rem(44)} minmax(0, 1fr) ${rem(36)};
  gap: ${rem(10)};
  align-items: start;
  padding-block: ${rem(12)};
  padding-inline: ${rem(TRIP_LIST_MOBILE_INLINE_PADDING)};
  border-block-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.is-selected {
    background: ${props => `color-mix(in srgb, ${props.theme.palette.highlight.primary.lightBackground}, ${props.theme.palette.neutral.surface.normalBackground} 64%)`};
  }

  &.is-clickable {
    cursor: pointer;
  }

  &.is-selection-mode > *:not(.is-selection-control) {
    pointer-events: none;
  }

  &.is-draggable {
    cursor: grab;
  }

  /* Only opacity, so picking a card up doesn't animate its background or border
     when selection state changes at the same time. */
  transition: opacity 140ms ease-out;

  &.is-dragging {
    opacity: 0.4;
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 1ms;
  }

  &.is-drop-target-before,
  &.is-drop-target-after {
    z-index: 2;
  }

  &.is-drop-target-before::before,
  &.is-drop-target-after::before {
    content: "";
    position: absolute;
    inset-inline-start: 0;
    inset-inline-end: 0;
    height: ${rem(3)};
    border-radius: ${props => props.theme.borderRadius.round};
    background: ${props => props.theme.palette.neutral.surface.normalContrast};
    pointer-events: none;
  }

  &.is-drop-target-before::before {
    top: 0;
  }

  &.is-drop-target-after::before {
    bottom: 0;
  }

  &.is-drag-overlay {
    border-block-end: none;
    border-radius: ${props => props.theme.borderRadius.S};
    box-shadow: ${props => props.theme.shadow.bottom.medium};
  }
`

const RowHandleSlot = styled.div`
  position: relative;
  display: flex;
  min-height: ${rem(44)};
  align-items: center;
  justify-content: center;
`

const DRAG_HANDLE_DOT_RADIUS = rem(1.25)
const DRAG_HANDLE_DOT_BOX = rem(3)
const DRAG_HANDLE_DOT_GAP = rem(5)

const DragHandle = styled.span`
  width: ${rem(8)};
  height: ${rem(8)};
  transform: translate(${rem(6)}, ${rem(-8)});
  background-image: ${(props) => {
    const dot = `radial-gradient(circle, ${props.theme.palette.neutral.surface.lightForegroundAlt} ${DRAG_HANDLE_DOT_RADIUS}, transparent ${DRAG_HANDLE_DOT_RADIUS})`
    return `${dot}, ${dot}, ${dot}, ${dot}`
  }};
  background-size: ${DRAG_HANDLE_DOT_BOX} ${DRAG_HANDLE_DOT_BOX};
  background-position:
    0 0,
    ${DRAG_HANDLE_DOT_GAP} 0,
    0 ${DRAG_HANDLE_DOT_GAP},
    ${DRAG_HANDLE_DOT_GAP} ${DRAG_HANDLE_DOT_GAP};
  background-repeat: no-repeat;
`

const SelectionCheckbox = styled(TripListCheckboxButton)`
  &.size-small {
    width: ${rem(18)};
    height: ${rem(18)};
    transform: translate(${rem(6)}, ${rem(-8)});
  }

  input {
    border-radius: ${rem(4)};
  }

  input:not(:checked):not(:indeterminate) {
    border-width: ${rem(2)};
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  input:not(:checked):not(:indeterminate):hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const IconSlot = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: ${rem(2)};
`

const CardTitleStack = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${rem(2)};
`

const CardMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  flex-wrap: nowrap;
  margin-block-start: ${rem(4)};
`

const PriceSlot = styled.div`
  display: flex;
  min-width: 0;
  margin-inline-start: auto;
  justify-content: flex-end;
`

function TripListMobileRowCard({
  row,
  tripStops,
  selected,
  selectionMode,
  luxPlusEnabled,
  isBookingPending,
  isNoteUpdatePending,
  isStatusUpdatePending,
  notesDisabled,
  statusDisabled,
  actionsDisabled,
  dragDisabled: dragDisabledProp = false,
  isDragOverlay = false,
  dropTargetPlacement,
  onSelectRow,
  onRowClick,
  onBookRow,
  onViewBooking,
  onAskAi,
  onAskAiMove,
  onDelete,
  onMoveToDay,
  onUpdateNote,
  onUpdateStatus,
}: Props) {
  const intl = useIntl()
  const [noteEditing, setNoteEditing] = useState(false)
  const canView = row.viewTarget.kind !== 'none'
  // Eligibility is structural, so the handle stays put while a row is transiently
  // undraggable (streaming, a pending note/status write) instead of flickering out.
  const dragEligible = !selectionMode && !row.isBooked && canMoveTripListRowToAnotherDay(row)
  const dragDisabled = (
    dragDisabledProp ||
    actionsDisabled ||
    isBookingPending ||
    isNoteUpdatePending ||
    isStatusUpdatePending ||
    !dragEligible
  )
  // Only the drag listeners are spread onto the card. dnd-kit's `attributes` would
  // also make it a focusable `role="button"`, which the mobile card deliberately is
  // not - the whole card is a tap target and its actions live in the overflow menu.
  // Nothing is lost: no trip planner drag surface registers a KeyboardSensor, so
  // those attributes only ever contributed ARIA that keyboard dragging can't honour.
  const {
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    // The overlay copy must not share the real card's id - see getDragOverlayDraggableId.
    id: isDragOverlay ? getDragOverlayDraggableId(row.id) : row.id,
    disabled: dragDisabled,
  })

  const selectLabel = intl.formatMessage(
    {
      id: 'uEW07qIriZwmUSm6ljqV',
      defaultMessage: 'Select {title}',
      description: 'Accessible label for selecting a row in the mobile TripChat itinerary list view',
    },
    { title: row.title },
  )
  const handleSelectChange = useCallback(() => {
    onSelectRow(row.id, !selected)
  }, [onSelectRow, row.id, selected])

  const handleControlClick = useCallback((event: MouseEvent) => {
    if (selectionMode) return
    event.stopPropagation()
  }, [selectionMode])

  const handleCheckboxClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  const handleViewClick = useCallback(() => {
    if (actionsDisabled || !canView) return
    onRowClick(row)
  }, [actionsDisabled, canView, onRowClick, row])

  const handleEditNoteClick = useCallback(() => {
    if (notesDisabled) return
    setNoteEditing(true)
  }, [notesDisabled])

  const handleNoteChange = useCallback((notes: string | undefined) => {
    if (notesDisabled || isNoteUpdatePending) return
    onUpdateNote(row, notes)
  }, [isNoteUpdatePending, notesDisabled, onUpdateNote, row])

  const handleStatusChange = useCallback((status: TripListStatusValue) => {
    if (statusDisabled || isStatusUpdatePending) return
    onUpdateStatus(row, status)
  }, [isStatusUpdatePending, onUpdateStatus, row, statusDisabled])

  const handleCardClick = useCallback(() => {
    if (selectionMode) {
      handleSelectChange()
      return
    }
    if (canView) {
      handleViewClick()
    }
  }, [canView, handleSelectChange, handleViewClick, selectionMode])

  const isCardClickable = selectionMode || canView

  return (
    <Card
      ref={setNodeRef}
      onClick={isCardClickable ? handleCardClick : undefined}
      className={clsx({
        'is-selected': selected,
        'is-clickable': isCardClickable,
        'is-selection-mode': selectionMode,
        'is-draggable': !dragDisabled,
        'is-dragging': isDragging,
        'is-drag-overlay': isDragOverlay,
        'is-drop-target-before': dropTargetPlacement === 'before',
        'is-drop-target-after': dropTargetPlacement === 'after',
      })}
      data-testid={isDragOverlay ? undefined : `trip-list-mobile-row-${row.id}`}
      // The overlay copy is a floating visual, never a drop target. Emitting the
      // hit-test attribute would put a duplicate row id under the pointer, masked only
      // by `pointer-events: none` on its wrapper.
      data-trip-list-row-id={isDragOverlay ? undefined : row.id}
      data-trip-list-drag-disabled={dragDisabled ? 'true' : undefined}
      data-stop-id={row.stop.id}
      data-day-id={row.day?.id}
      data-item-id={row.kind === 'item' ? row.item.id : row.option.itemId}
      {...(!dragDisabled ? listeners : undefined)}
    >
      <RowHandleSlot
        className={selectionMode ? 'is-selection-control' : undefined}
        onClick={handleControlClick}
      >
        {selectionMode && (
          <SelectionCheckbox
            size="small"
            checked={selected}
            onClick={handleCheckboxClick}
            onChange={handleSelectChange}
            aria-label={selectLabel}
          />
        )}
        {dragEligible && <DragHandle aria-hidden="true" />}
      </RowHandleSlot>
      <IconSlot>
        <TripListTypeIcon type={row.rowType} row={row} />
      </IconSlot>
      <CardTitleStack>
        <BodyText variant="medium" weight="bold" lineClamp={2}>
          {row.title}
        </BodyText>
        {row.subtitle && (
          <Caption variant="large" colour="light-foreground-alt" lineClamp={2}>
            {row.subtitle}
          </Caption>
        )}
        {(row.note || noteEditing) && (
          <TripListRowNote
            note={row.note}
            title={row.title}
            disabled={notesDisabled || selectionMode}
            saving={isNoteUpdatePending}
            editing={noteEditing}
            onEditingChange={setNoteEditing}
            onChange={handleNoteChange}
          />
        )}
        <CardMetaRow onClick={handleControlClick}>
          <TripListRowStatus
            status={row.status}
            title={row.title}
            disabled={selectionMode || statusDisabled}
            saving={isStatusUpdatePending}
            onChange={handleStatusChange}
          />
          <PriceSlot>
            <TripListRowPrice price={row.price} luxPlusEnabled={luxPlusEnabled} compact />
          </PriceSlot>
        </CardMetaRow>
      </CardTitleStack>
      {!selectionMode && (
        <TripListMobileRowActionsMenu
          row={row}
          tripStops={tripStops}
          isBookingPending={isBookingPending}
          notesDisabled={notesDisabled}
          actionsDisabled={actionsDisabled}
          onViewRow={handleViewClick}
          onBookRow={onBookRow}
          onViewBooking={onViewBooking}
          onAskAi={onAskAi}
          onAskAiMove={onAskAiMove}
          onDelete={onDelete}
          onMoveToDay={onMoveToDay}
          onEditNote={handleEditNoteClick}
        />
      )}
    </Card>
  )
}

// Every drop-target change re-renders the list, so without this each hover step
// re-renders every card instead of just the one the insertion line moved to. All
// props are stable during a drag (handlers are memoised upstream and the row
// objects come from a memo), so the shallow compare holds.
export default memo(TripListMobileRowCard)
